import React from 'react';
import { match, Redirect } from 'react-router';
import { Query } from '@apollo/client/react/components';
import { mapAfter } from 'apollo-relay/update-query';
import { toGlobalId } from 'graphql-relay';

import { RouteWithSearchRenderProps } from 'lib/react-router/route-with-search';
import LoadingProgress from 'lib/react-router/loading-progress';
import btoa from 'lib/base64/btoa';

import ErrorPage from 'four-nets/errors/error-page';
import query, { AlbumQuery, AlbumQueryVariables } from 'four-nets/photoblog/posts/album/album-query.graphql';

import { PostType } from './detail-loader.graphql.types';
import Detail from './detail';

export const FIRST = 100;

interface Params {
    slug: string;
    username: string;
}

export interface Props {
    match: match<Params>;
}

export interface Props extends RouteWithSearchRenderProps<Params, { offset: number }> {}

class DetailLoader extends React.PureComponent<Props> {
    imagesToUpload: File[] = [];

    render() {
        const { hash, state } = this.props.location;
        const { username, slug } = this.props.match.params;
        const { offset } = this.props.search;

        const matches = hash.match(/#comment-(\d+)/);
        let commentId: string | undefined;
        if (matches) {
            commentId = toGlobalId('CommentNode', matches[1]!);
        }

        let after: string | undefined;
        if (offset) {
            after = btoa(`arrayconnection:${offset - 1}`) || undefined;
        }

        let first: number | undefined = FIRST;
        if (state && state.ommitFirst) {
            first = undefined;
        }

        return (
            <Query<AlbumQuery, AlbumQueryVariables>
                query={query}
                variables={{ slug, username, after, first }}
                errorPolicy='all'
                fetchPolicy={'cache-first'}
            >
                {({ error, data, loading, refetch, fetchMore }) => {
                    let errorWithCode = null;
                    if (error) {
                        const gqlError = error?.graphQLErrors[0];
                        if (gqlError?.extensions?.code === 401) {
                            errorWithCode = { ...error, code: 401 };
                        } else if (gqlError?.extensions?.code == 404) {
                            errorWithCode = { ...error, code: 404 };
                        } else {
                            errorWithCode = { ...error, code: 500 };
                        }
                    } else if (!loading && (!data || !data.postBySlug)) {
                        errorWithCode = { ...new Error(), code: 404 };
                    }

                    return (
                        <LoadingProgress loading={loading}>
                            <ErrorPage error={errorWithCode}>
                                {data && data.postBySlug && data.postBySlug.postType === 'POST_ALBUM' ? (
                                    <Detail
                                        {...this.props}
                                        pathname={this.props.location.pathname}
                                        fetchMore={
                                            data.postBySlug.images && data.postBySlug.images.pageInfo.hasNextPage
                                                ? (all: boolean = true) => {
                                                      const endCursor = data!.postBySlug!.images!.pageInfo.endCursor;

                                                      return fetchMore({
                                                          variables: {
                                                              slug,
                                                              after: endCursor,
                                                              first: all ? undefined : FIRST,
                                                          },
                                                          updateQuery: (
                                                              previousResult = {} as AlbumQuery,
                                                              {
                                                                  fetchMoreResult = {} as AlbumQuery,
                                                              }: { fetchMoreResult?: AlbumQuery }
                                                          ) => {
                                                              return {
                                                                  postBySlug: {
                                                                      ...fetchMoreResult.postBySlug,
                                                                      images: mapAfter(
                                                                          previousResult!.postBySlug!.images,
                                                                          fetchMoreResult!.postBySlug!.images
                                                                      ),
                                                                  },
                                                              } as AlbumQuery;
                                                          },
                                                      });
                                                  }
                                                : undefined
                                        }
                                        refetch={refetch}
                                        commentId={commentId}
                                        {...data.postBySlug}
                                    />
                                ) : data && data.postBySlug && data.postBySlug.postType === 'POST_ARTICLE' ? (
                                    <Redirect to={data.postBySlug.href} />
                                ) : null}
                            </ErrorPage>
                        </LoadingProgress>
                    );
                }}
            </Query>
        );
    }
}

export default DetailLoader;
